import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Trash2, Lightbulb, CheckCircle, ChevronDown, ChevronUp, Plus, Home, ChefHat } from 'lucide-react';
import { VARIANTS, KITCHEN_PACKAGES, SUB_ITEM_TYPES, createDefaultSubItem } from './constants';
import SubItemInput from './SubItemInput';

export const RoomInput = ({ room, index, onUpdate, onRemove, canRemove, isCollapsed: initialCollapsed = false, globalStyle = 'modern' }) => {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapsed);
  const isKitchen = room.room_type === 'kitchen';
  
  // Sync internal state with prop when it changes
  useEffect(() => {
    setIsCollapsed(initialCollapsed);
  }, [initialCollapsed]);

  // Add a new sub-item to this room
  const addSubItem = (itemType = 'wardrobe') => {
    const newSubItem = createDefaultSubItem(itemType);
    const currentSubItems = room.sub_items || [];
    onUpdate(index, 'sub_items', [...currentSubItems, newSubItem]);
  };

  // Update a sub-item
  const updateSubItem = (subIndex, field, value) => {
    const updatedSubItems = [...(room.sub_items || [])];
    updatedSubItems[subIndex] = { ...updatedSubItems[subIndex], [field]: value };
    
    // Auto-update item name when type changes
    if (field === 'item_type') {
      const config = SUB_ITEM_TYPES.find(t => t.value === value);
      if (config) {
        updatedSubItems[subIndex].item_name = config.label;
        updatedSubItems[subIndex].frontage_length = config.defaultFrontage;
        updatedSubItems[subIndex].height = config.defaultHeight;
        updatedSubItems[subIndex].add_mirror = value === 'dresser' || value === 'vanity';
      }
    }
    
    onUpdate(index, 'sub_items', updatedSubItems);
  };

  // Remove a sub-item
  const removeSubItem = (subIndex) => {
    const updatedSubItems = (room.sub_items || []).filter((_, i) => i !== subIndex);
    onUpdate(index, 'sub_items', updatedSubItems);
  };

  // Get summary for collapsed state
  const getSummary = () => {
    if (isKitchen) {
      const pkg = KITCHEN_PACKAGES.find(p => p.value === room.kitchen_package);
      return `${room.frontage_length}ft running • ${pkg?.name || 'Basic'}`;
    }
    const itemCount = (room.sub_items || []).length;
    const itemNames = (room.sub_items || []).slice(0, 3).map(i => i.item_name).join(', ');
    return itemCount > 0 ? itemNames + (itemCount > 3 ? ` +${itemCount - 3} more` : '') : 'No items';
  };

  // Get current style label
  const styleLabel = VARIANTS.find(v => v.value === globalStyle)?.label || 'Modern';

  return (
    <div className="bg-white border rounded-xl shadow-sm overflow-hidden">
      {/* Room Header - Always visible, clickable to expand/collapse */}
      <div 
        className={`flex items-center justify-between p-4 cursor-pointer hover:bg-gray-50 transition-colors ${isCollapsed ? '' : 'border-b'}`}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isKitchen ? 'bg-amber-100' : 'bg-primary/10'}`}>
            {isKitchen ? (
              <ChefHat className="w-5 h-5 text-amber-600" />
            ) : (
              <Home className="w-5 h-5 text-primary" />
            )}
          </div>
          <div>
            <span className="font-semibold text-lg">{room.room_name}</span>
            {isCollapsed && (
              <p className="text-sm text-gray-500">{getSummary()}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {canRemove && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onRemove(index);
              }}
              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              title="Remove room"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
          {isCollapsed ? (
            <ChevronDown className="w-5 h-5 text-gray-400" />
          ) : (
            <ChevronUp className="w-5 h-5 text-gray-400" />
          )}
        </div>
      </div>

      {/* Collapsible Content */}
      {!isCollapsed && (
        <div className="p-4 space-y-4">
          {/* Room Name Row */}
          <div className="grid grid-cols-1 gap-4">
            <div>
              <Label className="text-xs">Room Name</Label>
              <Input
                type="text"
                value={room.room_name}
                onChange={(e) => onUpdate(index, 'room_name', e.target.value)}
                className="mt-1"
                placeholder="e.g., Master Bedroom"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Kitchen-specific inputs */}
          {isKitchen && (
            <>
              {/* Kitchen Dimensions */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs">Running Length (ft)</Label>
                  <Input
                    type="number"
                    min="1"
                    step="0.5"
                    className="rounded-lg mt-1 h-10"
                    value={room.frontage_length}
                    onChange={(e) => onUpdate(index, 'frontage_length', parseFloat(e.target.value) || 0)}
                    data-testid="kitchen-running-length"
                  />
                  <p className="text-xs text-gray-500 mt-1">Total counter length</p>
                </div>
                <div>
                  <Label className="text-xs">Height (ft)</Label>
                  <Input
                    type="number"
                    min="1"
                    step="0.5"
                    className="rounded-lg mt-1 h-10"
                    value={room.height}
                    onChange={(e) => onUpdate(index, 'height', parseFloat(e.target.value) || 0)}
                    placeholder="9.5"
                  />
                </div>
                <div>
                  <Label className="text-xs">Lighting</Label>
                  <label className="flex items-center gap-2 cursor-pointer p-2.5 bg-amber-50 rounded-lg border border-amber-200 w-full h-10 mt-1">
                    <input
                      type="checkbox"
                      checked={room.add_profile_lighting || false}
                      onChange={(e) => onUpdate(index, 'add_profile_lighting', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-amber-600"
                    />
                    <Lightbulb className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-800">Profile Light</span>
                  </label>
                </div>
              </div>

              {/* Tall Uppers / Loft Options - Mutually Exclusive */}
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold mb-3 block">Upper Cabinet Options</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Tall Uppers Option */}
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Toggle tall_uppers, clear add_loft if enabling tall_uppers
                      // Use batch update to ensure atomicity
                      const newTallUppers = !room.tall_uppers;
                      onUpdate(index, { 
                        tall_uppers: newTallUppers, 
                        add_loft: newTallUppers ? false : room.add_loft 
                      });
                    }}
                    className={`relative p-4 rounded-xl border-2 text-left transition-all cursor-pointer select-none ${
                      room.tall_uppers
                        ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/20'
                        : 'bg-white border-gray-300 hover:border-primary/50 hover:bg-primary/5 hover:shadow-sm'
                    }`}
                    data-testid="tall-uppers-btn"
                    role="button"
                    tabIndex={0}
                  >
                    {room.tall_uppers && (
                      <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-primary" />
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-sm text-gray-900">Tall Uppers</span>
                    </div>
                    <p className="text-xs text-gray-600">Extended upper cabinets reaching ceiling height</p>
                    {room.tall_uppers && room.frontage_length > 0 && (
                      <p className="text-xs font-medium text-primary mt-2">
                        Area: {(room.frontage_length * 3).toFixed(1)} sqft
                      </p>
                    )}
                  </div>

                  {/* Add Loft Option */}
                  <div
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      // Toggle add_loft, clear tall_uppers if enabling add_loft
                      // Use batch update to ensure atomicity
                      const newAddLoft = !room.add_loft;
                      onUpdate(index, { 
                        add_loft: newAddLoft, 
                        tall_uppers: newAddLoft ? false : room.tall_uppers 
                      });
                    }}
                    className={`relative p-4 rounded-xl border-2 text-left transition-all cursor-pointer select-none ${
                      room.add_loft
                        ? 'border-primary bg-primary/10 shadow-md ring-2 ring-primary/20'
                        : 'bg-white border-gray-300 hover:border-primary/50 hover:bg-primary/5 hover:shadow-sm'
                    }`}
                    data-testid="add-loft-btn"
                    role="button"
                    tabIndex={0}
                  >
                    {room.add_loft && (
                      <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-primary" />
                    )}
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-sm text-gray-900">Add Loft</span>
                    </div>
                    <p className="text-xs text-gray-600">Separate loft storage above standard uppers</p>
                    {room.add_loft && room.frontage_length > 0 && (
                      <p className="text-xs font-medium text-primary mt-2">
                        Area: {(room.frontage_length * 3).toFixed(1)} sqft
                      </p>
                    )}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2 italic">* Select one option - Tall Uppers or Loft (mutually exclusive)</p>
              </div>

              {/* Kitchen Package Selection */}
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold mb-3 block">Select Kitchen Package</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {KITCHEN_PACKAGES.map(pkg => (
                    <button
                      key={pkg.value}
                      type="button"
                      onClick={() => onUpdate(index, 'kitchen_package', pkg.value)}
                      className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                        room.kitchen_package === pkg.value
                          ? 'border-primary bg-primary/5 shadow-md'
                          : `${pkg.color} hover:border-gray-300`
                      }`}
                      data-testid={`kitchen-pkg-${pkg.value}`}
                    >
                      {room.kitchen_package === pkg.value && (
                        <CheckCircle className="absolute top-2 right-2 w-5 h-5 text-primary" />
                      )}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-semibold text-sm">{pkg.name}</span>
                        <span className="text-xs text-gray-500">{pkg.priceIndicator}</span>
                      </div>
                      <p className="text-xs text-gray-600 mb-2">{pkg.description}</p>
                      <ul className="text-xs text-gray-500 space-y-1">
                        {pkg.features.slice(0, 3).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-1">
                            <span className="text-primary mt-0.5">•</span>
                            <span>{feature}</span>
                          </li>
                        ))}
                        {pkg.features.length > 3 && (
                          <li className="text-primary font-medium">+{pkg.features.length - 3} more features</li>
                        )}
                      </ul>
                    </button>
                  ))}
                </div>
              </div>

              {/* Shutter Add-ons - Configured at Design Center */}
              <div className="border-t pt-4">
                <Label className="text-sm font-semibold mb-3 block">Shutter Add-ons (Optional)</Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    room.shutter_addons?.glass ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="checkbox"
                      checked={room.shutter_addons?.glass || false}
                      onChange={(e) => onUpdate(index, 'shutter_addons', { ...(room.shutter_addons || {}), glass: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary"
                    />
                    <div>
                      <span className="font-medium text-sm">Glass Shutters</span>
                      <p className="text-xs text-gray-500">Elegant glass-fronted doors</p>
                    </div>
                  </label>
                  
                  <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    room.shutter_addons?.traditional ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="checkbox"
                      checked={room.shutter_addons?.traditional || false}
                      onChange={(e) => onUpdate(index, 'shutter_addons', { ...(room.shutter_addons || {}), traditional: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary"
                    />
                    <div>
                      <span className="font-medium text-sm">Traditional Shutters</span>
                      <p className="text-xs text-gray-500">Classic wood-panel design</p>
                    </div>
                  </label>
                  
                  <label className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    room.shutter_addons?.acrylic ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
                  }`}>
                    <input
                      type="checkbox"
                      checked={room.shutter_addons?.acrylic || false}
                      onChange={(e) => onUpdate(index, 'shutter_addons', { ...(room.shutter_addons || {}), acrylic: e.target.checked })}
                      className="w-4 h-4 rounded border-gray-300 text-primary"
                    />
                    <div>
                      <span className="font-medium text-sm">Acrylic Shutters</span>
                      <p className="text-xs text-gray-500">High-gloss modern finish</p>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-amber-600 mt-2 bg-amber-50 p-2 rounded border border-amber-200">
                  * Shutter add-ons are configured at the Design Center appointment. The current quote does not include these costs.
                </p>
              </div>

              {/* Optional Upgrades Note */}
              <div className="border-t pt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-800 mb-2">Optional Upgrades Available at Design Center</p>
                  <p className="text-xs text-blue-700">
                    Additional kitchen customization options such as specialty handles (Gola profiles, bar pulls, edge-pull handles), 
                    drawer systems (tandem boxes, bottle pull-outs, corner units), functional hardware (soft-close hinges, lift-up systems), 
                    and accessories can be chosen during your Design Center appointment. Costs will vary based on your style selections.
                  </p>
                </div>
              </div>
            </>
          )}

          {/* General Room - Sub-items */}
          {!isKitchen && (
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-semibold">Items in this Room</Label>
              </div>
              
              {/* Sub-items List */}
              <div className="space-y-3">
                {(room.sub_items || []).map((item, subIdx) => (
                  <SubItemInput
                    key={subIdx}
                    item={item}
                    index={subIdx}
                    onUpdate={updateSubItem}
                    onRemove={removeSubItem}
                    canRemove={(room.sub_items || []).length > 1}
                  />
                ))}
              </div>
              
              {/* Add Sub-item Button */}
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addSubItem('wardrobe')}
                  className="gap-1 text-xs"
                >
                  <Plus className="w-3 h-3" /> Wardrobe
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addSubItem('dresser')}
                  className="gap-1 text-xs"
                >
                  <Plus className="w-3 h-3" /> Dresser
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addSubItem('tv_unit')}
                  className="gap-1 text-xs"
                >
                  <Plus className="w-3 h-3" /> TV Unit
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addSubItem('study_desk')}
                  className="gap-1 text-xs"
                >
                  <Plus className="w-3 h-3" /> Study Desk
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addSubItem('pooja')}
                  className="gap-1 text-xs"
                >
                  <Plus className="w-3 h-3" /> Pooja
                </Button>
                <Select onValueChange={(v) => v && addSubItem(v)}>
                  <SelectTrigger className="w-[120px] h-8 text-xs">
                    <SelectValue placeholder="More..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="feature_wall">Feature Wall</SelectItem>
                    <SelectItem value="shoe_rack">Shoe Rack</SelectItem>
                    <SelectItem value="crockery_unit">Crockery Unit</SelectItem>
                    <SelectItem value="bar_unit">Bar Unit</SelectItem>
                    <SelectItem value="vanity">Vanity Unit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* False Ceiling Option */}
          <div className="border-t pt-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={room.include_false_ceiling || false}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  // Use batch update to enable both settings at once
                  const updates = {
                    include_false_ceiling: isChecked
                  };
                  // Auto-enable profile lights for Modern style and up when false ceiling is enabled
                  if (isChecked && globalStyle && ['modern', 'traditional', 'luxury'].includes(globalStyle)) {
                    updates.add_ceiling_profile_lights = true;
                  }
                  onUpdate(index, updates);
                }}
                className="w-5 h-5 rounded border-gray-300 text-primary"
              />
              <div>
                <span className="font-medium text-sm">Add False Ceiling</span>
                <p className="text-xs text-gray-500">Designer ceiling with cove lighting</p>
              </div>
            </label>
            
            {room.include_false_ceiling && (
              <div className="mt-3 ml-8 space-y-3">
                <div>
                  <Label className="text-xs">False Ceiling Area (sqft)</Label>
                  <Input
                    type="number"
                    min="0"
                    className="rounded-lg mt-1 h-10 max-w-[200px]"
                    value={room.false_ceiling_sqft || 0}
                    onChange={(e) => onUpdate(index, 'false_ceiling_sqft', parseFloat(e.target.value) || 0)}
                    placeholder="Enter ceiling area"
                  />
                </div>
                
                {/* Ceiling Profile Lights Add-on (for Modern style and up) */}
                {globalStyle && ['modern', 'traditional', 'luxury'].includes(globalStyle) && (
                  <label className="flex items-center gap-2 cursor-pointer p-2.5 bg-amber-50 rounded-lg border border-amber-200 max-w-fit">
                    <input
                      type="checkbox"
                      checked={room.add_ceiling_profile_lights || false}
                      onChange={(e) => onUpdate(index, 'add_ceiling_profile_lights', e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-amber-600"
                    />
                    <Lightbulb className="w-4 h-4 text-amber-600" />
                    <span className="text-xs font-medium text-amber-800">Add Ceiling Profile Lights</span>
                    <span className="text-xs text-amber-600">(Recommended for {globalStyle} style)</span>
                  </label>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default RoomInput;
